import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GetSemanticModelApplicationErrors,
  ListSemanticModelsApplicationErrors,
  ListSemanticModelsResponse,
  QuerySemanticModelDataApplicationErrors,
  QuerySemanticModelDataOptions,
  QuerySemanticModelDataResponse,
  SemanticModel,
  getSemanticModel as universalGetSemanticModel,
  listSemanticModels as universalListSemanticModels,
  querySemanticModelData as universalQuerySemanticModelData,
} from './analytics-semantic-model-v3-semantic-model-analytics-semantic-model.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/analytics-semantic-model' };

export function querySemanticModelData(
  httpClient: HttpClient
): QuerySemanticModelDataSignature {
  return (
    semanticModelId: string,
    options?: NonNullablePaths<
      QuerySemanticModelDataOptions,
      `fields` | `interval`,
      0
    >
  ) =>
    universalQuerySemanticModelData(
      semanticModelId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QuerySemanticModelDataSignature {
  /**
   * Retrieves data from a semantic model for the specified fields, filters, sorting, and paging.
   *
   *
   * Query Semantic Model Data runs with these defaults, which you can override:
   *
   * - `paging.limit` is `50`
   * - `paging.offset` is `0`
   *
   * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
   * @param - ID of the semantic model to query.
   * @returns Response for querying data from a semantic model.
   */
  (
    semanticModelId: string,
    options?: NonNullablePaths<
      QuerySemanticModelDataOptions,
      `fields` | `interval`,
      0
    >
  ): Promise<
    NonNullablePaths<QuerySemanticModelDataResponse, `results`, 0> & {
      __applicationErrorsType?: QuerySemanticModelDataApplicationErrors;
    }
  >;
}

export function getSemanticModel(
  httpClient: HttpClient
): GetSemanticModelSignature {
  return (semanticModelId: string) =>
    universalGetSemanticModel(
      semanticModelId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetSemanticModelSignature {
  /**
   * Retrieves a semantic model by ID.
   * @param - ID of the semantic model to retrieve.
   * @returns Retrieved semantic model.
   */
  (semanticModelId: string): Promise<
    NonNullablePaths<
      SemanticModel,
      | `_id`
      | `slug`
      | `measures`
      | `measures.${number}.name`
      | `measures.${number}.type`
      | `dimensions`
      | `parameters`
      | `keywords`,
      2
    > & {
      __applicationErrorsType?: GetSemanticModelApplicationErrors;
    }
  >;
}

export function listSemanticModels(
  httpClient: HttpClient
): ListSemanticModelsSignature {
  return () =>
    universalListSemanticModels(
      // @ts-ignore
      { httpClient }
    );
}

interface ListSemanticModelsSignature {
  /**
   * Retrieves a list of all available semantic models.
   * @returns Response for listing all available semantic models.
   */
  (): Promise<
    NonNullablePaths<
      ListSemanticModelsResponse,
      | `semanticModels`
      | `semanticModels.${number}._id`
      | `semanticModels.${number}.slug`,
      2
    > & {
      __applicationErrorsType?: ListSemanticModelsApplicationErrors;
    }
  >;
}

export {
  DataRow,
  DateTimeInterval,
  Field,
  FieldFilter,
  FieldFilters,
  FieldTypeEnum,
  FieldValue,
  FieldValueValueOneOf,
  FilterConditionEnum,
  FilterPrefixEnum,
  GetSemanticModelRequest,
  GetSemanticModelResponse,
  ListSemanticModelsRequest,
  ListSemanticModelsResponse,
  Paging,
  PagingMetadata,
  QuerySemanticModelDataOptions,
  QuerySemanticModelDataRequest,
  QuerySemanticModelDataResponse,
  SemanticModel,
  SortOrder,
  Sorting,
} from './analytics-semantic-model-v3-semantic-model-analytics-semantic-model.universal.js';
